import { Injectable } from '@angular/core';
import { LiveStreamSDK, LogLevel, SESSION_MODES, StreamSessionConfig, StreamMode } from '@rideview/livestream-web-sdk';
import { Subject } from 'rxjs';
import { API } from '@app-core/constants/api.constants';
import { AccessService } from '@app-core/services/access/access.service';
import { DeviceStatePayload } from '@app-live-view/constants/live-view.constants';
@Injectable({
  providedIn: 'root',
})
export class LiveStreamSdkService {
  private sdk: LiveStreamSDK;
  public connectionState$ = new Subject<string>();
  public error$ = new Subject<any>();
  public sessionConflict$ = new Subject<any>();
  public callStatus$ = new Subject<string>();
  public callResponse$ = new Subject<any>();
  public callEnd$ = new Subject<void>();
  public leave$ = new Subject<void>();
  public deviceAudioAlert$ = new Subject<any>();
  public networkStats$ = new Subject<any>();
  public callSummary$ = new Subject<any>();
  public muteStatus$ = new Subject<boolean>();
  public deviceState$ = new Subject<DeviceStatePayload>();

  constructor(private accessService: AccessService) {
    this.sdk = new LiveStreamSDK({
      mode: SESSION_MODES.SDK_MANAGED,
      apiBaseUrl: API.INTERMEDIATE_SERVER_URL,
      headers: {
        'X-Access-Token': this.accessService.getLoginInfo().token,
      },
      logLevel: LogLevel.ERROR,
      enableTelemetry: false,
    });

    this.sdk.on('connected', () => {
      this.connectionState$.next('connected');
    });
    this.sdk.on('playing', () => this.connectionState$.next('playing'));
    this.sdk.on('buffering', () => this.connectionState$.next('buffering'));
    this.sdk.on('device-connected', () => this.connectionState$.next('device-connected'));
    this.sdk.on('current-device-state', (payload: DeviceStatePayload) => this.deviceState$.next(payload));
    this.sdk.on('call-status', (status) => this.callStatus$.next(status));
    this.sdk.on('call-response', (payload) => {
      this.callResponse$.next(payload);
    });

    this.sdk.on('call-end', () => this.callEnd$.next());
    this.sdk.on('leave', () => {
      this.connectionState$.next('disconnected');
      this.leave$.next();
    });
    this.sdk.on('device-audio-alert', (payload) => this.deviceAudioAlert$.next(payload));
    this.sdk.on('network-stats', (stats) => this.networkStats$.next(stats));
    this.sdk.on('call-summary', (summary) => this.callSummary$.next(summary));
    this.sdk.on('mute-status', (isMuted) => this.muteStatus$.next(!!isMuted));
    this.sdk.on('error', (err) => {
      console.error('Live Stream SDK Error:', { ...err });
      this.error$.next(err);
    });
  }

  async startStreaming(config: StreamSessionConfig, mode: StreamMode) {
    try {
      const res: any = await this.sdk.start(config, mode);
      return { ...res, streamRequestId: res.currentSessionId };
    } catch (err) {
      console.error('Error starting livestream:', { ...err });
      if (err?.cause?.data?.conflictingRequestId) {
        this.sessionConflict$.next({
          conflictingRequestId: err.cause.data.conflictingRequestId,
          config,
          mode,
        });
      } else {
        this.error$.next(err);
      }
      throw err;
    }
  }

  attach(mediaElement: HTMLVideoElement | HTMLAudioElement) {
    this.sdk.attach(mediaElement);
  }

  async stopStreaming() {
    try {
      await this.sdk.stop();
    } catch (err) {
      this.error$.next(err);
      throw err;
    }
  }
  async joinLivestream(conflictingRequestId: string, fleetId: string, mode: StreamMode) {
    try {
      const res: any = await this.sdk.join(conflictingRequestId, fleetId, mode);
      return { ...res, streamRequestId: res.currentSessionId };
    } catch (err) {
      this.error$.next(err);
      throw err;
    }
  }

  async startAudioCall() {
    try {
      await this.sdk.startAudioCall();
    } catch (err) {
      this.error$.next(err);
      throw err;
    }
  }

  async stopAudioCall() {
    try {
      await this.sdk.stopAudioCall();
    } catch (err) {
      this.error$.next(err);
      throw err;
    }
  }

  muteAudio() {
    this.sdk.muteMic();
    try {
      this.sdk.enableDriverAudio();
    } catch (err) {
      this.error$.next(err);
    }
  }

  unmuteAudio() {
    this.sdk.unmuteMic();
    try {
      this.sdk.disableDriverAudio();
    } catch (err) {
      this.error$.next(err);
    }
  }

  async activateMic() {
    try {
      await this.sdk.activateMic();
    } catch (err) {
      this.error$.next(err);
      throw err;
    }
  }

  async deactivateMic() {
    try {
      await this.sdk.deactivateMic();
    } catch (err) {
      this.error$.next(err);
      throw err;
    }
  }

  getLocalStream(): MediaStream | undefined {
    return this.sdk.getLocalStream?.();
  }

  getRemoteStream(): MediaStream | undefined {
    return this.sdk.getRemoteStream?.();
  }
}
